// ---------------------------------------------------------------------------
// DnD Email Editor – HTML → Model Parser & Model Version Migration
//
// Parses saved HTML back into a DndEmailModel using data-ftp-* attributes.
// Also provides migration for upgrading old model versions.
// ---------------------------------------------------------------------------

import type { DndEmailModel, Layer, Element, Column, ColumnsLayer, Spacing } from './dnd-model';
import { CURRENT_EDITOR_VERSION } from './dnd-model';
import { isDndHtml } from './dnd-marker';
import { LAYOUT_COLUMN_COUNTS, reconcileColumns } from './dnd-columns';
import {
  createEmptyModel,
  defaultSpacing,
  defaultBorderSides,
  createTextLayer,
  createImageLayer,
  createButtonLayer,
  createDividerLayer,
  createSpacerLayer,
  createHtmlLayer,
  createColumnsLayer,
  createSocialLayer,
  createVideoLayer,
  createCountdownLayer,
  defaultColumn,
  createTextElement,
  createImageElement,
  createButtonElement,
  createDividerElement,
  createSpacerElement,
  createHtmlElement,
} from './dnd-defaults';

// ---- Model migration ------------------------------------------------------

export function migrateModel(raw: Record<string, unknown>): DndEmailModel {
  const model = raw as unknown as DndEmailModel;
  const version = model.editorVersion ?? 0;

  if (version < 1) {
    const empty = createEmptyModel();
    return {
      ...empty,
      ...model,
      editorVersion: CURRENT_EDITOR_VERSION,
      globalStyles: { ...empty.globalStyles, ...(model.globalStyles ?? {}) },
      wrapper: { ...empty.wrapper, ...(model.wrapper ?? {}) },
      preheaderText: model.preheaderText ?? '',
      layers: (model.layers ?? []).map(migrateLayer),
    };
  }

  return {
    ...model,
    editorVersion: CURRENT_EDITOR_VERSION,
    layers: (model.layers ?? []).map(migrateLayer),
  };
}

function migrateLayer(layer: Layer): Layer {
  const defaults: Partial<Layer> = {
    label: (layer as Layer).label ?? '',
    locked: (layer as Layer).locked ?? true,
    bgImage: (layer as Layer).bgImage ?? '',
    bgRepeat: (layer as Layer).bgRepeat ?? 'no-repeat',
    bgPosition: (layer as Layer).bgPosition ?? 'center center',
    bgSize: (layer as Layer).bgSize ?? 'cover',
    contentBgColor: (layer as Layer).contentBgColor ?? '',
    hideOnMobile: (layer as Layer).hideOnMobile ?? false,
    hideOnDesktop: (layer as Layer).hideOnDesktop ?? false,
    border: (layer as Layer).border ?? defaultBorderSides(),
    padding: (layer as Layer).padding ?? defaultSpacing(10),
    margin: (layer as Layer).margin ?? defaultSpacing(0),
  };

  const migrated = { ...defaults, ...layer } as Layer;

  if (migrated.type === 'columns') {
    const colsLayer = migrated as ColumnsLayer;
    const cols = (colsLayer.columns ?? []).map(migrateColumn);
    colsLayer.columnsGap = colsLayer.columnsGap ?? 10;
    colsLayer.stackOnMobile = colsLayer.stackOnMobile ?? true;
    // Heal models whose column count drifted from the declared layout under the
    // old (buggy) inspector that changed `layout` without reconciling `columns`.
    colsLayer.columns = reconcileColumns(cols, LAYOUT_COLUMN_COUNTS[colsLayer.layout] ?? cols.length);
  }

  return migrated;
}

function migrateColumn(col: Column): Column {
  return {
    ...col,
    bgColor: col.bgColor ?? '',
    padding: col.padding ?? defaultSpacing(5),
    border: col.border ?? defaultBorderSides(),
    valign: col.valign ?? 'top',
    elements: (col.elements ?? []).map(migrateElement),
  };
}

/**
 * Fill in the fields an older saved model may be missing.
 *
 * The defaults come AFTER the spread, not before it. Before, the spread always
 * won — `Element` declares these fields required, so `...el` overwrote every
 * default and this function did nothing for them, which tsc reported as TS2783
 * and nobody acted on. A migration reads data written by an older version of
 * the model, so its input is genuinely partial whatever the type says; typing
 * it as such is the honest way to let the defaults apply.
 */
function migrateElement(el: Element): Element {
  const partial = el as Partial<Element>;
  return {
    ...partial,
    padding: partial.padding ?? defaultSpacing(5),
    border: partial.border ?? defaultBorderSides(),
    margin: partial.margin ?? defaultSpacing(0),
    hideOnMobile: partial.hideOnMobile ?? false,
    hideOnDesktop: partial.hideOnDesktop ?? false,
  } as Element;
}

// ---- HTML → Model parsing -------------------------------------------------

export function parseHtmlToModel(html: string): DndEmailModel | null {
  if (!isDndHtml(html)) return null;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return null;

    const model = createEmptyModel();

    const preheader = body.querySelector(
      'div[style*="display:none"], div[style*="display: none"]',
    );
    if (preheader) {
      model.preheaderText = preheader.textContent?.trim() ?? '';
    }

    const wrapper = body.querySelector('[data-ftp-type="wrapper"]');
    const layerContainer = wrapper
      ? wrapper.querySelector('td')
      : body;

    if (!layerContainer) return model;

    const layerNodes = layerContainer.querySelectorAll(
      ':scope > [data-ftp-type]:not([data-ftp-type="wrapper"])',
    );

    for (const node of layerNodes) {
      const layer = parseLayerNode(node as HTMLElement);
      if (layer) model.layers.push(layer);
    }

    return model;
  } catch {
    return null;
  }
}

function parseLayerNode(node: HTMLElement): Layer | null {
  const type = node.getAttribute('data-ftp-type');
  const id = node.getAttribute('data-ftp-node-id') ?? '';
  const locked = node.getAttribute('data-ftp-unlocked') !== 'true';

  switch (type) {
    case 'text': {
      const td = node.querySelector('td');
      return createTextLayer({
        id,
        locked,
        content: td?.innerHTML?.trim() ?? '',
        padding: parsePaddingFromStyle(td),
      });
    }

    case 'image': {
      const img = node.querySelector('img');
      const link = node.querySelector('a');
      return createImageLayer({
        id,
        locked,
        src: img?.getAttribute('src') ?? '',
        alt: img?.getAttribute('alt') ?? '',
        width: parseInt(img?.getAttribute('width') ?? '600', 10),
        href: link?.getAttribute('href') ?? '',
        padding: parsePaddingFromStyle(node.querySelector('td')),
      });
    }

    case 'button': {
      const link = node.querySelector('a');
      return createButtonLayer({
        id,
        locked,
        text: link?.textContent?.trim() ?? '',
        href: link?.getAttribute('href') ?? '#',
        padding: parsePaddingFromStyle(node.querySelector(':scope > tr > td')),
      });
    }

    case 'divider':
      return createDividerLayer({ id, locked });

    case 'spacer':
      return createSpacerLayer({ id, locked });

    case 'html': {
      const td = node.querySelector('td');
      return createHtmlLayer({
        id,
        locked,
        content: td?.innerHTML?.trim() ?? '',
      });
    }

    case 'columns': {
      const layout =
        (node.getAttribute('data-ftp-column-layout') as ColumnsLayer['layout']) ?? '2';
      const columnNodes = node.querySelectorAll('[data-ftp-type="column"]');
      const columns: Column[] = [];
      for (const colNode of columnNodes) {
        columns.push(parseColumnNode(colNode as HTMLElement));
      }
      return createColumnsLayer({
        id,
        locked,
        layout,
        columns: columns.length ? columns : undefined,
      });
    }

    case 'social':
      return {
        ...createSocialLayerStub(),
        id,
        locked,
      } as Layer;

    case 'video':
      return {
        ...createVideoLayerStub(),
        id,
        locked,
      } as Layer;

    case 'countdown':
      return {
        ...createCountdownLayerStub(),
        id,
        locked,
      } as Layer;

    default:
      return null;
  }
}

function parseColumnNode(node: HTMLElement): Column {
  const col = defaultColumn();
  col.id = node.getAttribute('data-ftp-node-id') ?? col.id;

  const elementNodes = node.querySelectorAll('[data-ftp-type]');
  for (const elNode of elementNodes) {
    const el = parseElementNode(elNode as HTMLElement);
    if (el) col.elements.push(el);
  }

  return col;
}

function parseElementNode(node: HTMLElement): Element | null {
  const type = node.getAttribute('data-ftp-type');
  const id = node.getAttribute('data-ftp-node-id') ?? '';

  switch (type) {
    case 'text':
      return createTextElement({
        id,
        content: node.innerHTML?.trim() ?? '',
      });

    case 'image': {
      const img = node.querySelector('img');
      const link = node.querySelector('a');
      return createImageElement({
        id,
        src: img?.getAttribute('src') ?? '',
        alt: img?.getAttribute('alt') ?? '',
        width: parseInt(img?.getAttribute('width') ?? '300', 10),
        href: link?.getAttribute('href') ?? '',
      });
    }

    case 'button': {
      const link = node.querySelector('a');
      return createButtonElement({
        id,
        text: link?.textContent?.trim() ?? '',
        href: link?.getAttribute('href') ?? '#',
      });
    }

    case 'divider':
      return createDividerElement({ id });

    case 'spacer':
      return createSpacerElement({ id });

    case 'html':
      return createHtmlElement({
        id,
        content: node.innerHTML?.trim() ?? '',
      });

    default:
      return null;
  }
}

function parsePaddingFromStyle(el: HTMLElement | null): Spacing {
  if (!el) return defaultSpacing(10);
  const style = el.getAttribute('style') ?? '';
  const match = style.match(/padding:\s*(\d+)px\s+(\d+)px\s+(\d+)px\s+(\d+)px/);
  if (match) {
    return {
      top: parseInt(match[1], 10),
      right: parseInt(match[2], 10),
      bottom: parseInt(match[3], 10),
      left: parseInt(match[4], 10),
    };
  }
  return defaultSpacing(10);
}

// Stub factories for extended block types parsed from HTML
// (imported at top of file alongside other defaults)
function createSocialLayerStub() {
  return createSocialLayer();
}
function createVideoLayerStub() {
  return createVideoLayer();
}
function createCountdownLayerStub() {
  return createCountdownLayer();
}

// ---- Sync locked-user edits back into JSON --------------------------------

export function syncUnlockedEdits(
  model: DndEmailModel,
  editedHtml: string,
): DndEmailModel {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editedHtml, 'text/html');
    const unlocked = doc.querySelectorAll('[data-ftp-unlocked="true"]');

    let updated = { ...model, layers: [...model.layers] };

    for (const node of unlocked) {
      const nodeId = node.getAttribute('data-ftp-node-id');
      const type = node.getAttribute('data-ftp-type');
      if (!nodeId || !type) continue;

      if (type === 'text') {
        const td = node.querySelector('td');
        const content = td?.innerHTML?.trim() ?? node.innerHTML?.trim() ?? '';
        updated = patchFieldById(updated, nodeId, { content });
      } else if (type === 'image') {
        const img = node.querySelector('img');
        if (img) {
          updated = patchFieldById(updated, nodeId, {
            src: img.getAttribute('src') ?? '',
            alt: img.getAttribute('alt') ?? '',
          });
        }
      }
    }

    return updated;
  } catch {
    return model;
  }
}

function patchFieldById(
  model: DndEmailModel,
  id: string,
  patch: Record<string, string>,
): DndEmailModel {
  const layers = model.layers.map((layer) => {
    if (layer.id === id) return { ...layer, ...patch };

    if (layer.type === 'columns') {
      const cols = (layer as ColumnsLayer).columns.map((col) => ({
        ...col,
        elements: col.elements.map((el) =>
          el.id === id ? { ...el, ...patch } : el,
        ),
      }));
      return { ...layer, columns: cols } as Layer;
    }

    return layer;
  });

  return { ...model, layers };
}
